import Cookies from 'js-cookie';

import { startMetaPixel, stopMetaPixel } from './metaPixel';

const COOKIE_NAME = 'acceptCookies';
// The visitor's answer, accept or decline, is kept this long; the notice comes back only after it expires
const CHOICE_DAYS = 150;

// Google's consent mode signals. gtag.js only exists once the visitor has accepted; a later decline denies them all.
const GOOGLE_STORAGE = [
  'analytics_storage',
  'ad_storage',
  'ad_user_data',
  'ad_personalization',
];
const googleConsent = (value) =>
  Object.fromEntries(GOOGLE_STORAGE.map((key) => [key, value]));

// Microsoft Clarity project "Meu Treino" (Claudio Pipe's Clarity account, since 27/10/2026), for both sites
export const CLARITY_PROJECT_ID = 'z4h8zj46lb';

export const hasConsent = () => Cookies.get(COOKIE_NAME) === 'true';

export const CONSENT_CHANGED_EVENT = 'consentChanged';

export const grantConsent = () => {
  Cookies.set(COOKIE_NAME, 'true', { expires: CHOICE_DAYS });
  initAnalyticsOnConsent();
  window.dispatchEvent(new CustomEvent(CONSENT_CHANGED_EVENT, { detail: { consent: true } }));
};

// A declined notice stays answered: the cookie keeps "false" instead of disappearing
export const revokeConsent = () => {
  Cookies.set(COOKIE_NAME, 'false', { expires: CHOICE_DAYS });
  disableAnalytics();
  window.dispatchEvent(new CustomEvent(CONSENT_CHANGED_EVENT, { detail: { consent: false } }));
};

export const hasMadeChoice = () => Cookies.get(COOKIE_NAME) !== undefined;

/**
 * Google, only once the visitor has accepted: gtag.js and AdSense load, and Analytics records the page
 * view. Called on acceptance and, for a visitor who accepted before, on every page load (main.jsx).
 * Undecided or declined, nothing is requested from Google.
 */
export const startGoogleWithConsent = () => {
  loadGoogleAnalytics();
  loadAdSense();
};

const loadGoogleAnalytics = () => {
  const id = import.meta.env.VITE_GA_MEASUREMENT_ID;
  if (!id) return;
  // Accepting again after a decline in the same visit: lift the switch-off and grant consent again
  window[`ga-disable-${id}`] = false;
  if (document.querySelector('script[data-gtag]')) {
    window.gtag?.('consent', 'update', googleConsent('granted'));
    return;
  }
  window.dataLayer = window.dataLayer || [];
  // gtag's own stub: it must push the arguments object itself
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };
  window.gtag('consent', 'default', googleConsent('granted'));
  window.gtag('js', new Date());
  // The config call sends this page's page view; later routes send theirs through trackPageView
  window.gtag('config', id);
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
  script.dataset.gtag = 'true';
  document.head.appendChild(script);
};

const loadAdSense = () => {
  const client = import.meta.env.VITE_ADSENSE_CLIENT;
  if (!client || document.querySelector('script[data-adsense]')) return;
  const script = document.createElement('script');
  script.async = true;
  script.crossOrigin = 'anonymous';
  script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${client}`;
  script.dataset.adsense = 'true';
  document.head.appendChild(script);
};

const initAnalyticsOnConsent = () => {
  startGoogleWithConsent();
  // Meta Pixel (meutreino.fit only): the ad campaigns' signups
  startMetaPixel();

  // Heap Analytics — load if not already loaded
  if (!window.heap?.appid) {
    const isLocalhost = window.location.hostname === 'localhost';
    const heapId = isLocalhost ? '3307113423' : '1715548186';
    loadHeap(heapId);
  }

  // Microsoft Clarity — init if not already running
  if (!window.clarity) {
    import('@microsoft/clarity').then(({ default: Clarity }) => {
      Clarity.init(CLARITY_PROJECT_ID);
    });
  }
};

const disableAnalytics = () => {
  stopMetaPixel();

  // Google Analytics and AdSense — revoke consent, and stop Analytics sending anything more this visit
  if (window.gtag) {
    window.gtag('consent', 'update', googleConsent('denied'));
  }
  const gaId = import.meta.env.VITE_GA_MEASUREMENT_ID;
  if (gaId) window[`ga-disable-${gaId}`] = true;

  // Clear GA cookies
  document.cookie.split(';').forEach((cookie) => {
    const name = cookie.split('=')[0].trim();
    if (name.startsWith('_ga') || name.startsWith('_gid')) {
      Cookies.remove(name, { path: '/' });
      Cookies.remove(name, { path: '/', domain: window.location.hostname });
      Cookies.remove(name, {
        path: '/',
        domain: '.' + window.location.hostname,
      });
    }
  });
};

const loadHeap = (appId) => {
  window.heap = window.heap || [];
  window.heap.load = (e, config) => {
    window.heap.appid = e;
    window.heap.config = config || {};
    const r = document.createElement('script');
    r.type = 'text/javascript';
    r.async = true;
    r.src = 'https://cdn.heapanalytics.com/js/heap-' + e + '.js';
    const a = document.getElementsByTagName('script')[0];
    a.parentNode.insertBefore(r, a);
    const methods = [
      'addEventProperties',
      'addUserProperties',
      'clearEventProperties',
      'identify',
      'resetIdentity',
      'removeEventProperty',
      'setEventProperties',
      'track',
      'unsetEventProperty',
    ];
    methods.forEach((m) => {
      window.heap[m] = (...args) => window.heap.push([m, ...args]);
    });
  };
  window.heap.load(appId, { replayEnabled: false });
};
